import { useMemo, useState, useCallback, useRef } from "react";
import { ArrowLeft, Download, ExternalLink, FileText, Loader2, Maximize2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { openExternalBrowser } from "@/lib/externalBrowser";
import { PdfCanvasViewer } from "@/components/common/PdfCanvasViewer";
import { istGoogleQuelle } from "@/lib/pdfQuelle";
import { useIsNativeApp } from "@/hooks/useIsNativeApp";

/** Detect if running on a mobile device or in a WebView where PDF iframes don't work */
export const isMobileOrWebView = (): boolean => {
  const ua = navigator.userAgent || '';
  const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
  const isWebViewUA = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua)
    || /wv|WebView|Web2Native|MusiKonzept/i.test(ua);
  // iPadOS 13+ tarnt sich als "Macintosh" – echte Macs haben keine Touch-Punkte
  const isIpadDesktopUA = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  return isMobile || isWebViewUA || isIpadDesktopUA;
};

interface PdfViewerUrls {
  embedUrl: string;
  openUrl: string;
  downloadUrl: string;
}

export const buildPdfViewerUrls = (url: string, avoidDirectPdfIframe: boolean): PdfViewerUrls => {
  const getDriveFileId = (u: string) => {
    const fileMatch = u.match(/drive\.google\.com\/file\/d\/([^/]+)/);
    const openMatch = u.match(/[?&]id=([^&]+)/);
    return fileMatch?.[1] ?? openMatch?.[1] ?? null;
  };

  const driveId = getDriveFileId(url);
  const isDirectPdf = !driveId && /\.pdf(\?|$)/i.test(url);

  const embedUrl = driveId
    ? `https://drive.google.com/file/d/${driveId}/preview`
    : isDirectPdf && !avoidDirectPdfIframe
      ? url
      : `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`;

  const openUrl = driveId ? `https://drive.google.com/file/d/${driveId}/view` : url;
  const downloadUrl = driveId
    ? `https://drive.google.com/uc?export=download&id=${driveId}`
    : url;

  return { embedUrl, openUrl, downloadUrl };
};

interface PdfViewerProps {
  url: string;
  fileName: string;
  fileSize?: number | null;
  allowDownload?: boolean | null;
  className?: string;
  showEmbed?: boolean;
}

/**
 * Dunkles Teal für die Download-Knöpfe (Frage 237): Weiß auf dem Primär-Teal `#359297` hat nur 3,68:1,
 * auf `#227277` 5,61:1. Nur hier gesetzt, nicht in `index.css` (das änderte jeden Knopf der Seite).
 */
const DOWNLOAD_FARBE = "bg-[#227277] text-white hover:bg-[#1B5E62]";

/**
 * Download a file via fetch+blob so iOS can show the native share/save dialog.
 * If the blob path fails, fall back to the external browser bridge.
 */
export async function downloadFile(url: string, fileName: string): Promise<void> {
  try {
    toast.info("Download wird vorbereitet…");

    const response = await fetch(url);
    if (!response.ok) throw new Error("Download fehlgeschlagen");

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    }, 1000);

    toast.success("Download gestartet");
  } catch (err) {
    console.error("Download error:", err);
    toast.info("PDF wird im Browser geöffnet…");
    openExternalBrowser(url);
  }
}

export function PdfViewer({
  url,
  fileName,
  fileSize,
  allowDownload = true,
  className,
  showEmbed = true,
}: PdfViewerProps) {
  const [embedFailed, setEmbedFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [embedActivated, setEmbedActivated] = useState(true);
  const [isMobileOverlay, setIsMobileOverlay] = useState(false);
  const [forceEmbed, setForceEmbed] = useState(false);
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const loadedRef = useRef(false);

  const [canvasGescheitert, setCanvasGescheitert] = useState(false);

  const isMobile = useMemo(() => isMobileOrWebView(), []);
  const isNativeApp = useIsNativeApp();
  // „Download erlauben“ gilt überall gleich: am Computer im Kopf und in der
  // Ersatzanzeige, in der App und auf iPad/iPhone in der Karte. Bis 06.10.2026
  // zeigte der Computer den Knopf auch bei „aus“, und niemand merkte, dass die
  // App ihn bei rund 700 Lektionen versteckte. Nur false sperrt, null nicht.
  const downloadErlaubt = allowDownload !== false;
  const useSafariFallback = isMobile && !forceEmbed;

  // Eigene Anzeige über pdf.js statt des Browser-PDF-Programms. Sie braucht
  // eine Quelle, die CORS erlaubt — unser Supabase Storage tut das, Google
  // Drive nicht. Für Drive-Links und bei Fehlern bleibt der iframe-Weg.
  const nutzeCanvas = useMemo(
    () => !istGoogleQuelle(url) && !canvasGescheitert,
    [url, canvasGescheitert]
  );

  // Give the iframe a timeout to load; if it doesn't fire onLoad, show fallback
  const startLoadTimeout = useCallback(() => {
    if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
    loadTimeoutRef.current = setTimeout(() => {
      if (!loadedRef.current) {
        setEmbedFailed(true);
        setIsLoading(false);
      }
    }, 8000);
  }, []);


  const handleIframeLoad = useCallback(() => {
    loadedRef.current = true;
    if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
    setIsLoading(false);
    setEmbedFailed(false);
  }, []);

  const handleIframeError = useCallback(() => {
    if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
    setEmbedFailed(true);
    setIsLoading(false);
  }, []);

  const { embedUrl, openUrl, downloadUrl } = useMemo(
    () => buildPdfViewerUrls(url, isMobile),
    [isMobile, url]
  );

  const formatFileSize = (size: number) => {
    if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
    return `${(size / 1024).toFixed(0)} KB`;
  };

  const handleOpenPdf = () => {
    if (useSafariFallback) {
      openExternalBrowser(openUrl);
      return;
    }

    if (isMobile) {
      // On mobile/WebView: open in a fullscreen overlay instead of new tab
      setIsMobileOverlay(true);
      setIsLoading(true);
      loadedRef.current = false;
    } else {
      window.open(openUrl, "_blank");
    }
  };

  const handleDownload = () => {
    void downloadFile(downloadUrl, fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`);
  };

  const handleCloseMobileOverlay = () => {
    setIsMobileOverlay(false);
    setIsLoading(true);
    loadedRef.current = false;
  };

  const shouldShowEmbed = showEmbed && embedActivated;

  // Mobile fullscreen overlay with back button
  if (isMobileOverlay) {
    return (
      <div className="fixed inset-0 z-[100] bg-background flex flex-col">
        {/* Header with back button and download */}
        <div className="flex items-center justify-between gap-2 p-3 border-b border-border bg-card shadow-sm safe-area-top">
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1.5 text-foreground"
            onClick={handleCloseMobileOverlay}
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Zurück</span>
          </Button>

          <h3 className="text-sm font-medium truncate flex-1 text-center px-2">
            {fileName}
          </h3>

        </div>

        {/* PDF content */}
        <div className="flex-1 relative">
          {nutzeCanvas ? (
            <PdfCanvasViewer
              url={url}
              className="h-full"
              onError={() => setCanvasGescheitert(true)}
            />
          ) : (
          <>
          {isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Dokument wird geladen...</span>
              </div>
            </div>
          )}
          <iframe
            src={embedUrl}
            ref={(el) => {
              if (el && isLoading) startLoadTimeout();
            }}
            className="w-full h-full border-0 bg-background"
            title={fileName}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
          </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "w-full max-w-4xl mx-auto overflow-hidden rounded-2xl border border-border/50 bg-card shadow-lg",
        className
      )}
    >
      {/* Kopfzeile — sitzt in derselben Karte wie die Anzeige darunter */}
      <div className="relative overflow-hidden bg-gradient-to-br from-card via-card to-primary/5">
        <div className="absolute top-0 right-0 h-32 w-32 -translate-y-1/2 translate-x-1/2 rounded-full bg-gradient-to-br from-primary/10 to-transparent blur-2xl" />

        <div className="relative px-4 py-3 sm:px-5 sm:py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-gradient-to-br from-primary/20 via-primary/10 to-accent/10 shadow-inner">
              <FileText className="h-5 w-5 text-primary" />
            </div>

            <div className="min-w-0 flex-1">
              <h3 className="truncate pr-4 text-base font-semibold leading-tight text-foreground">{fileName}</h3>
              <p className="text-xs text-muted-foreground">
                {fileSize ? formatFileSize(fileSize) : "PDF-Dokument"}
              </p>
            </div>

            <div className="flex flex-shrink-0 items-center gap-2">
              {/* On mobile: "Öffnen" opens the in-app overlay; on desktop: opens new tab */}
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                onClick={handleOpenPdf}
              >
                <ExternalLink className="mr-1.5 h-4 w-4" />
                <span className="hidden sm:inline">{isMobile ? "Öffnen" : "Neuer Tab"}</span>
              </Button>

              {!isMobile && downloadErlaubt && (
                <Button variant="default" size="sm" className={DOWNLOAD_FARBE} onClick={handleDownload}>
                  <Download className="mr-1.5 h-4 w-4" />
                  <span className="hidden sm:inline">Download</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {shouldShowEmbed && useSafariFallback && (
        <div className="border-t border-border/50 bg-muted/30 p-8 text-center space-y-4">
          <FileText className="h-16 w-16 text-primary/40 mx-auto" />
          <h4 className="text-base font-semibold text-foreground">{fileName}</h4>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {isNativeApp
              ? "Lade das PDF herunter, um es in deiner Noten-App, in iCloud Drive oder in Dateien zu speichern. Oder sieh es dir direkt hier in der Vorschau an."
              : "PDFs öffnest du auf dem iPad am besten im Browser. Dort kannst du sie über das Teilen-Symbol in deine Noten-App, in iCloud Drive oder in Dateien speichern."}
          </p>
          <div className="flex flex-col gap-2 items-center">
            {downloadErlaubt && (
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                <Button
                  variant="default"
                  size="lg"
                  className={cn("w-full sm:w-auto", DOWNLOAD_FARBE)}
                  onClick={handleDownload}
                >
                  <Download className="mr-2 h-5 w-5" />
                  PDF herunterladen
                </Button>
                {/* In der App öffnet sich hier kein echter Browser, sondern ein
                    Fenster ohne Zurück-Knopf — deshalb dort ausgeblendet. */}
                {!isNativeApp && (
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto"
                    onClick={() => openExternalBrowser(openUrl)}
                  >
                    <ExternalLink className="mr-2 h-5 w-5" />
                    Im Browser öffnen
                  </Button>
                )}
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground"
              onClick={() => setForceEmbed(true)}
            >
              Vorschau hier laden
            </Button>
          </div>
        </div>
      )}

      {shouldShowEmbed && !useSafariFallback && embedFailed && (
        <div className="border-t border-border/50 bg-muted/30 p-8 text-center space-y-4">
          <FileText className="h-16 w-16 text-primary/40 mx-auto" />
          <p className="text-sm text-muted-foreground">
            PDF-Vorschau konnte nicht geladen werden.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              variant="default"
              size="lg"
              className="w-full sm:w-auto"
              onClick={handleOpenPdf}
            >
              <ExternalLink className="mr-2 h-5 w-5" />
              PDF öffnen
            </Button>
            {!isMobile && downloadErlaubt && (
              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto"
                onClick={handleDownload}
              >
                <Download className="mr-2 h-5 w-5" />
                Download
              </Button>
            )}
          </div>
        </div>
      )}

      {shouldShowEmbed && !useSafariFallback && !embedFailed && (
        <div
          className={cn(
            "relative border-t border-border/50 bg-background",
            isFullscreen &&
              "fixed inset-4 z-50 overflow-hidden rounded-2xl border shadow-2xl"
          )}
        >
          {isLoading && !nutzeCanvas && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Dokument wird geladen...</span>
              </div>
            </div>
          )}

          {!nutzeCanvas && (
            <Button
              variant="secondary"
              size="icon"
              className="absolute right-3 top-3 z-20 bg-background/80 backdrop-blur-sm shadow-md hover:bg-background"
              onClick={() => setIsFullscreen((v) => !v)}
            >
              {isFullscreen ? <X className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          )}

          {isFullscreen && (
            <div
              className="fixed inset-0 -z-10 bg-background/80 backdrop-blur-sm"
              onClick={() => setIsFullscreen(false)}
            />
          )}

          <div className="w-full overflow-hidden" style={!isFullscreen ? { height: '80vh' } : { height: '100%' }}>
            {nutzeCanvas ? (
              <PdfCanvasViewer
                url={url}
                className="h-full"
                onError={() => setCanvasGescheitert(true)}
                toolbarExtras={
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setIsFullscreen((v) => !v)}
                    aria-label={isFullscreen ? "Vollbild beenden" : "Vollbild"}
                  >
                    {isFullscreen ? <X className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </Button>
                }
              />
            ) : (
              <iframe
                src={embedUrl}
                ref={(el) => {
                  if (el && isLoading) startLoadTimeout();
                }}
                className="bg-background border-0"
                style={{
                  width: '100%',
                  height: '100%',
                  // zoom schrumpfte das PDF-Programm des Browsers — nur im iframe-Weg nötig
                  ...(isFullscreen ? {} : { zoom: 0.75 }),
                }}
                title={fileName}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
